<template>
  <div v-if="modelValue" class="ui-pivot-table__panel">
    <UIButton
      v-tooltip.top="toggleTooltip"
      :leading="toggleIcon"
      size="xxs"
      variant="gray"
      fill="light"
      @click="handleToggle"
    />
    <UIButton
      v-tooltip.top="layoutTooltip"
      :leading="layoutIcon"
      size="xxs"
      variant="gray"
      fill="light"
      @click="handleChangeLayout"
    />
    <UIButton
      v-tooltip.top="collapseTooltip"
      :leading="collapseIcon"
      size="xxs"
      variant="gray"
      fill="light"
      @click="handleChangeCollapse"
    />
    <slot />
    <PanelSort
      v-model="modelValue"
      v-model:sort-cols="sortCols"
      v-model:sort-rows="sortRows"
      :columns-fields="sortColumnsFields"
      :rows-fields="sortRowsFields"
    />
    <UIButton
      v-tooltip.top="'Export to CSV'"
      :leading="ArrowUpTrayIcon"
      size="xxs"
      variant="gray"
      fill="light"
      @click="handleClickExport"
    />
  </div>
  <UISkeleton v-else class="ui-pivot-table__panel-skeleton" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { DropdownListData, Sort } from '@types'
import { PivotTableConfig } from '../utils/types'
import { PivotTableLayout } from '../utils/enums'

import { useResponsive } from '@/plugins/responsiveUI'

import PanelSort from './PanelSort.vue'
import { UISkeleton } from '@ui/skeletons'
import { UIButton } from '@ui/buttons'
import {
  ArrowUpTrayIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleUpIcon,
} from '@heroicons/vue/20/solid'
import { Bars4Icon, ViewColumnsIcon } from '@heroicons/vue/24/outline'
import CollapseAllIcon from '@/assets/images/icons/collapse-all.svg'
import ExpandAllIcon from '@/assets/images/icons/expand-all.svg'

type Props = {
  sortColumnsFields?: DropdownListData[]
  sortRowsFields?: DropdownListData[]
}

type Emits = {
  export: []
  expand: [data: boolean]
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const modelValue = defineModel<PivotTableConfig>()

const sortCols = defineModel<Sort[]>('sortCols')
const sortRows = defineModel<Sort[]>('sortRows')

const { isMobile } = useResponsive()

const isExpanded = ref(false)

const toggleIcon = computed(() => {
  if (isMobile.value) {
    return modelValue.value?.open ? ChevronDoubleUpIcon : ChevronDoubleDownIcon
  }
  return modelValue.value?.open ? ChevronDoubleLeftIcon : ChevronDoubleRightIcon
})

const layoutIcon = computed(() =>
  modelValue.value?.layout === PivotTableLayout.CLASSIC
    ? Bars4Icon
    : ViewColumnsIcon,
)

const collapseIcon = computed(() =>
  isExpanded.value
    ? CollapseAllIcon
    : ExpandAllIcon,
)

const toggleTooltip = computed(() =>
  modelValue.value?.open ? 'Close panel' : 'Open panel',
)

const layoutTooltip = computed(() =>
  modelValue.value?.layout === PivotTableLayout.CLASSIC
    ? 'Switch to modern layout'
    : 'Switch to classic layout',
)

const collapseTooltip = computed(() =>
  isExpanded.value
    ? 'Collapse all'
    : 'Expand all',
)

const handleToggle = () => {
  if (!modelValue.value) return
  const open = !modelValue.value.open
  modelValue.value = { ...modelValue.value, open }
}

const handleChangeLayout = () => {
  isExpanded.value = false
  let layout: PivotTableLayout
  if (modelValue.value?.layout === PivotTableLayout.CLASSIC) {
    layout = PivotTableLayout.MODERN
  } else {
    layout = PivotTableLayout.CLASSIC
  }
  modelValue.value = { ...modelValue.value, layout } as PivotTableConfig
}

const handleChangeCollapse = () => {
  isExpanded.value = !isExpanded.value
  emit('expand', isExpanded.value)
}

const handleClickExport = () => {
  emit('export')
}
</script>

<style>
.ui-pivot-table {
  &__panel {
    @apply h-8;
    @apply flex items-center gap-1;
    @apply bg-gray-100 dark:bg-gray-850;
    @apply mb-0.5;
  }

  &__panel-skeleton {
    @apply h-[2.125rem];
    @apply mb-0.5;
  }
}
</style>
